import { useEffect, useRef } from 'react';
import { useStylesheets } from 'hooks/useStylesheets';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import GlobalHeader from 'pages/dashboard/components/GlobalHeader';
import {
  useGetCmsAccessoryCatalogQuery,
  useGetCmsComponentCatalogQuery,
} from 'app/services/cmsApi';
import CmsNav from './components/CmsNav';
import CmsScreenHost from './components/CmsScreenHost';
import CmsFooter from './components/CmsFooter';
import { useCmsHashRoute } from './useCmsHashRoute';
import { useCmsFormControls } from './useCmsFormControls';
// Approved UX deviations from legacy, scoped per screen — see the file header.
import './cmsAdminOverrides.scss';

/**
 * ═════════════════════════════════════════════════════════════════════════════
 * CMS Admin (`/admin/` — ported from the Backbone app `cms_admin`) — the SHELL,
 * built in Phase 7 run 1.
 *
 * LIVE: `django/cms_admin/urls.py` has served this page via `ReactAppAuthView`
 * since the T10 switch (2026-08-14); the Backbone `CMSHomeView` was removed in
 * Phase 8. `/admin/` is ONE route and could not be half-switched, so it stayed
 * dormant (Backbone still served) through runs T1-T9 while the 31 feature
 * modules were ported screen by screen; all 67 non-dead registry screens are
 * now `live`.
 *
 * Run 1 deliberately shipped ZERO of the 31 feature modules. It shipped what all
 * 31 need in common:
 *   · the chrome and the two screen hosts        (`tmpl/cms_app.tmpl`)
 *   · the nav/tab system                         (`view/main.menu.js`,
 *                                                 `view/product.menu.js`)
 *   · the hash contract                          (`useCmsHashRoute.ts`)
 *   · the screen registry / per-module seam      (`cmsScreenRegistry.ts`)
 *   · the app-wide form-control layer            (`useCmsFormControls.ts`)
 *   · the data-layer conventions                 (`app/services/cmsApi.ts`)
 *   · the shared header with `appName="cms"`
 *
 * READ FIRST: `frontend/PHASE7_CMS_SURVEY.md` — the module map, the sequencing
 * plan, ~11,000 LOC of proven-dead code, and the five goal-doc premises that
 * measurement overturned.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * CSS — the exact legacy cascade, in order:
 *   header.css → cms_admin/skeleton.css → cms_admin/kinetix_ai.css   (the three
 *     `<head>` links, `templates/cms/styles.html:2-4`)
 *   → common.css → cms_admin/styles.css   (two `require.css!` calls in
 *     `mvc/app.js:1371-1372`, which land after the `<head>` links)
 * `react.html` already ships common.css + header.css globally; re-injecting all
 * five here (appended after the globals) reproduces the last-occurrence order.
 *
 * ★ NOT LOADED: `css/cms_admin/styles.min.css` (115 KB) — a stale minified
 * sibling of `styles.css` (942 KB) that no template and no `require.css!`
 * references.
 */
const CMS_CSS = [
  '/static/css/common/header.css',
  '/static/css/cms_admin/skeleton.css',
  '/static/css/cms_admin/kinetix_ai.css?v=1790587792',
  '/static/css/common/common.css',
  '/static/css/cms_admin/styles.css',
];

/**
 * `view/main.menu.js:44` builds the product menu with a hard-coded
 * `product_id: 1`, and `view/product.menu.js:39` fetches that product. Every
 * product-section hash is `#products/<this id>/...`.
 */
const CMS_BOOT_PRODUCT_ID = 1;

const CmsAdminPage = () => {
  // `templates/cms/cms_home.html:4` — `{% block title %} CMS {% endblock %}`.
  // Measured on the live page: `document.title === 'CMS'` and nothing rewrites
  // it per tab (unlike User Settings, where api.js sets a tab-qualified title).
  useDocumentTitle('CMS');

  // Gate first paint on the route CSS — the sheets are injected AFTER React
  // mounts, so without the gate the nav and the whole shell paint as raw
  // unstyled text in the load gap (the FOUC class fixed on dashboard/project/
  // sizer/partners/user-settings). Legacy loads skeleton.css render-blocking.
  const cssLoaded = useStylesheets(CMS_CSS);

  const { screenKey, openScreen } = useCmsHashRoute(CMS_BOOT_PRODUCT_ID);

  // The nav's product groups are data-driven: `product.menu.js:render_content`
  // fetches both catalogs and only then renders. Measured for mfg 1: 10
  // components, 17 accessories.
  const { data: componentCatalog } = useGetCmsComponentCatalogQuery();
  const { data: accessoryCatalog } = useGetCmsAccessoryCatalogQuery();

  /**
   * The app-wide form-control layer (`view/cms.main.js`, 386 LOC) is bound by
   * DELEGATION on `#ksdm-content`, exactly as legacy binds it — so it applies
   * to markup the shell has never seen, which is the whole point: 31 module
   * tranches inherit the number spinners, the required-field defaults, the
   * multi-select box and the decimal formatter without wiring anything.
   */
  const contentRef = useRef<HTMLDivElement>(null);
  useCmsFormControls(contentRef);

  /**
   * Height chain. `skeleton.css:72-82` gives `html` and `body` `height:100%`
   * (plus `overflow:hidden`), and `skeleton.css:182-186` sizes `#ksdm-content`
   * off it. `#react-root` sits between `body` and `#ksdm-content` and would
   * break the chain, so it is pinned to 100% — the same repair the reports,
   * AHU, sizer and user-settings routes use.
   */
  useEffect(() => {
    const root = document.getElementById('react-root');
    const prev = root?.style.height ?? '';
    if (root) root.style.height = '100%';
    return () => {
      if (root) root.style.height = prev;
    };
  }, []);

  return (
    <>
      {/*
        `header.tmpl` gates `app_name == 'cms'` in EXACTLY TWO places, both
        measured on the live page:
          1. `:6` — the logo `<a id="logo-img">` gets `target="_blank"`, because
             CMS Admin is itself opened in a new tab. **`cms` is the ONLY
             appName that does this.**
          2. `:35` — `#workflow_popup_button` is suppressed (same as
             dashboard/report/sizer/partners/user_settings).
        `header.tmpl:206`'s `{{#if user.is_cms_admin}}` guards a COMMENTED-OUT
        Admin submenu and is inert. Nothing else is gated: no nav item is
        `active`, and the AHU Sizer icon keeps its default rich-tooltip variant.
        `CmsBase.html:16` renders `<header id="ksdm-header" class="container-fluid">`.
      */}
      <GlobalHeader appName="cms" />

      {/*
        `CmsBase.html:20` — `<div id="ksdm-content" class="container-fluid">`.
        The height is `skeleton.css:182-186`'s `calc(100% - 60px)` + `-5px`
        margin, carried inline because the bundled dashboard CSS leaks
        `:global(body #ksdm-content){height:calc(100vh - 53px)}`
        (`StatusesRail.module.scss:22`), a higher-specificity rule that wins on
        every React route — the same route-scoped repair the AHU board, sizer
        and user-settings pages already use.
      */}
      <div
        id="ksdm-content"
        className="container-fluid"
        ref={contentRef}
        style={{ height: 'calc(100% - 60px)', marginTop: '-5px' }}
      >
        {cssLoaded && (
          <>
            {/* `tmpl/cms_app.tmpl:1-4`. ★ `#product_menues` stays EMPTY —
                `product.menu.js:render()` appends into `#main_menu nav`, not
                here (measured: `productMenuesHtmlLen: 0`). The div is kept
                because `product.menu.js:showThisMenu`/`showProductMod` still
                address it. */}
            <div id="cms-left-container" className="menu col-xs-2">
              <div id="main_menu" className="row">
                <CmsNav
                  activeKey={screenKey}
                  onOpen={openScreen}
                  componentCatalog={componentCatalog ?? []}
                  accessoryCatalog={accessoryCatalog ?? []}
                />
              </div>
              <div id="product_menues" className="row" />
            </div>

            {/* `tmpl/cms_app.tmpl:5-...` */}
            <div id="cms-container" className="main-container col-xs-10">
              <CmsScreenHost activeKey={screenKey} productId={CMS_BOOT_PRODUCT_ID} />

              {/* `tmpl/cms_app.tmpl:104-109` — the four-square spinner.
                  `api.js` shows it around every lazy screen load (`.show(100)`)
                  and fades it out on success (`.fadeOut(200)`), so on the live
                  page the STEADY state after boot is an inline
                  `style="display: none"` — measured. The template itself carries
                  no inline style and no loaded stylesheet has a
                  `#loading-center-absolute` rule (the one in `css/cms.css` is
                  never linked), so without this the element would sit
                  permanently "shown". Ships hidden; a tranche that needs the
                  spinner toggles it. */}
              <div id="loading-center-absolute" style={{ display: 'none' }}>
                <div className="object" id="object_four" />
                <div className="object" id="object_three" />
                <div className="object" id="object_two" />
                <div className="object" id="object_one" />
              </div>
            </div>
          </>
        )}
      </div>

      <CmsFooter />
    </>
  );
};

export default CmsAdminPage;
